import { useMemo, useState } from 'react'
import type { ActionOption } from '@v2/sdk'
import { ChoiceControl, type ChoiceHandler, type SelectionDraft } from './ChoiceControl'
import { Icon } from './icons'

interface ActionListProps {
  readonly actions: readonly ActionOption[]
  readonly onAction: ChoiceHandler
  readonly disabled?: boolean
  readonly compact?: boolean
  readonly label?: string
  /** Shared draft for set-controlled actions, so table clicks and this list agree. */
  readonly selection?: (controlId: string) => SelectionDraft | undefined
}

/** The same finite legal choices serve menus, inspection and keyboard access. */
export function ActionList({ actions, onAction, disabled = false, compact = false, label = 'Available actions', selection }: ActionListProps) {
  const [query, setQuery] = useState('')
  const unique = useMemo(() => [...new Map(actions.map(action => [action.id, action])).values()], [actions])
  const controls = new Map<string, ActionOption[]>()
  for (const action of unique) if (action.control) controls.set(action.control.id, [...(controls.get(action.control.id) ?? []), action])
  const plain = unique.filter(action => !action.control)
  const count = plain.length + controls.size
  // A changing decision may shrink below the search threshold; never retain an invisible filter.
  const search = count > 8 ? query.trim().toLocaleLowerCase() : ''
  const filtered = plain.filter(action => `${action.label} ${action.group ?? ''} ${action.description ?? ''}`.toLocaleLowerCase().includes(search))
  const visibleControls = [...controls].filter(([, choices]) => choices.some(action => `${action.control!.label} ${action.label} ${action.description ?? ''}`.toLocaleLowerCase().includes(search)))
  const groups = new Map<string, ActionOption[]>()
  for (const action of filtered) {
    const group = action.group ?? ''
    groups.set(group, [...(groups.get(group) ?? []), action])
  }
  if (!unique.length) return null
  const buttons = (choices: readonly ActionOption[]) => <div className="tt-action-buttons">{choices.map(action => <button className="tt-primary-button" key={action.id} title={action.description} disabled={disabled} onClick={() => onAction(action)}><span>{action.label}</span><Icon name="arrow" size={15}/></button>)}</div>
  return <div className={`tt-action-list${compact ? ' is-compact' : ''}`} aria-label={label}>
    {count > 8 && <label className="tt-choice-search"><span className="tt-sr-only">Search actions</span><input type="search" placeholder={`Search ${count} choices…`} value={query} onChange={event => setQuery(event.target.value)}/></label>}
    <div className="tt-choice-scroll">
      {visibleControls.map(([id, choices]) => <ChoiceControl key={id} actions={choices} onAction={onAction} disabled={disabled} selection={selection?.(id)}/>)}
      {groups.has('') && buttons(groups.get('')!)}
      {[...groups].filter(([group]) => group).map(([group, choices]) => <details className="tt-action-group" key={`${group}:${search ? 'search' : 'all'}`} open={search ? true : count <= 6}>
        <summary><span>{group}</span><span className="tt-choice-count">{choices.length}</span></summary>
        {buttons(choices)}
      </details>)}
      {!filtered.length && !visibleControls.length && <p className="tt-empty">No matching choices.</p>}
    </div>
  </div>
}
